import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, LogOut, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { clearToken, verifyAdmin } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";

const TABS = [
  { key: "projects", label: "Projects" },
  { key: "case-studies", label: "Case Studies" },
  { key: "leads", label: "Leads" },
  { key: "utm", label: "UTM Builder" },
];

const STATUSES = ["new", "contacted", "qualified", "archived"];

const TAB_TESTIDS = {
  projects: ADMIN.tabProjects,
  "case-studies": ADMIN.tabCaseStudies,
  leads: ADMIN.tabLeads,
  utm: "admin-tab-utm",
};

function statusBadgeClass(status) {
  if (status === "new") return "text-relatable border border-relatable";
  if (status === "qualified") return "text-bone border border-bone";
  return "text-fog border border-line";
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    verifyAdmin().then((ok) => {
      if (!ok) nav("/admin/login");
      else setAuthChecked(true);
    });
  }, [nav]);

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  if (!authChecked) {
    return <div className="min-h-screen bg-ink text-fog flex items-center justify-center eyebrow">Checking session…</div>;
  }

  return (
    <main data-testid={ADMIN.dashboard} className="min-h-screen bg-ink text-bone">
      <header className="border-b border-line px-6 md:px-12 py-5 flex items-center justify-between sticky top-0 bg-ink z-30">
        <div className="flex items-center gap-6">
          <span className="font-display text-xl tracking-tight">RELATABLE / Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="eyebrow text-fog hover:text-bone inline-flex items-center gap-2">
            View Site <ExternalLink className="w-3 h-3" />
          </a>
          <button
            onClick={logout}
            data-testid={ADMIN.logoutBtn}
            className="eyebrow text-fog hover:text-bone inline-flex items-center gap-2"
          >
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </header>

      <div className="border-b border-line px-6 md:px-12 flex gap-8">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={TAB_TESTIDS[t.key]}
            onClick={() => setTab(t.key)}
            className={`eyebrow py-5 border-b-2 -mb-px transition-colors ${
              tab === t.key ? "text-bone border-bone" : "text-fog border-transparent hover:text-bone"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-6 md:px-12 py-10">
        {tab === "projects" && <ProjectsTab />}
        {tab === "case-studies" && <CaseStudiesTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "utm" && <UtmBuilderTab />}
      </div>
    </main>
  );
}

/* ---------------- Projects ---------------- */
function emptyProject() {
  return { title: "", category: "", client: "", year: new Date().getFullYear(), description: "", vimeo_id: "", thumbnail: "", featured: false, order: 0, industry: "", division: "Studio", aspect: "16:9" };
}

function ProjectsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProject());

  const load = useCallback(async () => {
    const res = await api.get("/projects");
    setItems(res.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyProject(), ...p });
    setOpen(true);
  };
  const onNew = () => {
    setEditing(null);
    setForm(emptyProject());
    setOpen(true);
  };
  const onDelete = async (p) => {
    if (!confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/projects/${p.id}`);
    toast.success("Project deleted");
    load();
  };
  const onSave = async () => {
    try {
      if (editing) {
        await api.put(`/admin/projects/${editing.id}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/admin/projects", form);
        toast.success("Project created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl tracking-tight">Projects</h2>
          <p className="eyebrow text-fog mt-2">{items.length} entries</p>
        </div>
        <Button data-testid={ADMIN.newProjectBtn} onClick={onNew} className="bg-bone text-ink hover:bg-relatable hover:text-bone rounded-none">
          <Plus className="w-4 h-4 mr-2" /> New Project
        </Button>
      </div>

      <div className="border border-line">
        <table className="w-full text-sm">
          <thead className="bg-charcoal eyebrow text-fog">
            <tr>
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Division</th>
              <th className="text-left p-4">Aspect</th>
              <th className="text-left p-4">Year</th>
              <th className="text-left p-4">Vimeo</th>
              <th className="text-left p-4">Featured</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={ADMIN.projectRow} className="border-t border-line hover:bg-charcoal/50">
                <td className="p-4 font-display text-bone text-base">{p.title}</td>
                <td className="p-4 text-fog">{p.division || "—"}</td>
                <td className="p-4 text-fog font-mono text-xs">{p.aspect || "16:9"}</td>
                <td className="p-4 text-fog">{p.year || "—"}</td>
                <td className="p-4 text-fog font-mono text-xs">{p.vimeo_id}</td>
                <td className="p-4">{p.featured ? <span className="eyebrow text-relatable">FEATURED</span> : <span className="text-fog">—</span>}</td>
                <td className="p-4 text-right space-x-2">
                  <button onClick={() => onEdit(p)} className="text-fog hover:text-bone p-1"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(p)} className="text-fog hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink border-line text-bone max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {editing ? "Edit Project" : "New Project"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Title" v={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
            <Field label="Category" v={form.category} onChange={(v) => setForm({ ...form, category: v })} required />
            <Field label="Client" v={form.client} onChange={(v) => setForm({ ...form, client: v })} />
            <Field label="Year" type="number" v={form.year} onChange={(v) => setForm({ ...form, year: parseInt(v) || 0 })} />
            <Field label="Vimeo ID" v={form.vimeo_id} onChange={(v) => setForm({ ...form, vimeo_id: v })} required />
            <Field label="Industry" v={form.industry} onChange={(v) => setForm({ ...form, industry: v })} />
            <div>
              <Label className="eyebrow text-fog block mb-2">Division</Label>
              <select
                value={form.division}
                onChange={(e) => setForm({ ...form, division: e.target.value })}
                className="w-full bg-charcoal border border-line text-bone rounded-none px-3 py-2"
              >
                <option value="Marketing">Relatable Marketing</option>
                <option value="Studio">Relatable Studio</option>
                <option value="Experience">Relatable Experience</option>
              </select>
            </div>
            <div>
              <Label className="eyebrow text-fog block mb-2">Aspect Ratio</Label>
              <select
                value={form.aspect}
                onChange={(e) => setForm({ ...form, aspect: e.target.value })}
                className="w-full bg-charcoal border border-line text-bone rounded-none px-3 py-2"
              >
                <option value="16:9">16:9 (Horizontal)</option>
                <option value="9:16">9:16 (Vertical)</option>
              </select>
            </div>
            <Field label="Order" type="number" v={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} />
            <div className="flex items-center gap-3 mt-6">
              <Switch checked={!!form.featured} onCheckedChange={(c) => setForm({ ...form, featured: !!c })} />
              <Label className="eyebrow text-fog">Featured on homepage</Label>
            </div>
            <div className="col-span-2">
              <Label className="eyebrow text-fog block mb-2">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-charcoal border-line text-bone rounded-none"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-line text-fog rounded-none bg-transparent hover:bg-charcoal">Cancel</Button>
            <Button onClick={onSave} className="bg-bone text-ink hover:bg-relatable hover:text-bone rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", required }) {
  return (
    <div>
      <Label className="eyebrow text-fog block mb-2">{label}{required && " *"}</Label>
      <Input
        value={v ?? ""}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        className="bg-charcoal border-line text-bone rounded-none"
      />
    </div>
  );
}

/* ---------------- Case Studies ---------------- */
function emptyCS() {
  return { project_id: "", title: "", challenge: "", approach: "", strategy: "", production: "", execution: "", outcome: "", media_gallery: [], related_project_ids: [] };
}

function CaseStudiesTab() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCS());

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/case-studies"), api.get("/projects")]);
    setItems(a.data || []);
    setProjects(b.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    try {
      if (editing) {
        await api.put(`/admin/case-studies/${editing.id}`, form);
        toast.success("Case study updated");
      } else {
        await api.post("/admin/case-studies", form);
        toast.success("Case study created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };
  const onDelete = async (cs) => {
    if (!confirm(`Delete "${cs.title}"?`)) return;
    await api.delete(`/admin/case-studies/${cs.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl tracking-tight">Case Studies</h2>
          <p className="eyebrow text-fog mt-2">{items.length} entries</p>
        </div>
        <Button
          data-testid={ADMIN.newCaseStudyBtn}
          onClick={() => { setEditing(null); setForm(emptyCS()); setOpen(true); }}
          className="bg-bone text-ink hover:bg-relatable hover:text-bone rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" /> New Case Study
        </Button>
      </div>

      <div className="border border-line">
        <table className="w-full text-sm">
          <thead className="bg-charcoal eyebrow text-fog">
            <tr>
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Project</th>
              <th className="text-left p-4">Slug</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((cs) => {
              const project = projects.find((p) => p.id === cs.project_id);
              return (
                <tr key={cs.id} data-testid={ADMIN.caseStudyRow} className="border-t border-line">
                  <td className="p-4 font-display text-bone">{cs.title}</td>
                  <td className="p-4 text-fog">{project?.title || "—"}</td>
                  <td className="p-4 text-fog font-mono text-xs">{cs.slug}</td>
                  <td className="p-4 text-right space-x-2">
                    <button onClick={() => { setEditing(cs); setForm({ ...emptyCS(), ...cs }); setOpen(true); }} className="text-fog hover:text-bone p-1"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(cs)} className="text-fog hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-fog eyebrow">No case studies yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink border-line text-bone max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {editing ? "Edit Case Study" : "New Case Study"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="eyebrow text-fog block mb-2">Project *</Label>
              <select
                value={form.project_id}
                onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                className="w-full bg-charcoal border border-line text-bone rounded-none px-3 py-2"
              >
                <option value="">— select —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Field label="Title *" v={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
            </div>
            {["challenge","approach","strategy","production","execution","outcome"].map((k) => (
              <div className="col-span-2" key={k}>
                <Label className="eyebrow text-fog block mb-2 capitalize">{k}</Label>
                <Textarea
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="bg-charcoal border-line text-bone rounded-none"
                  rows={3}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-line text-fog rounded-none bg-transparent hover:bg-charcoal">Cancel</Button>
            <Button onClick={onSave} className="bg-bone text-ink hover:bg-relatable hover:text-bone rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Leads ---------------- */
function LeadsOverview({ items }) {
  // Aggregate lead counts by status, source, and 7-day trend.
  const stats = useMemo(() => {
    const byStatus = { new: 0, contacted: 0, qualified: 0, archived: 0 };
    const bySource = {}; // e.g. { contact: 12, discovery_call: 3 }
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const byDay = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfDay);
      d.setDate(startOfDay.getDate() - (6 - i));
      return { key: d.toISOString().slice(0, 10), label: d.toLocaleDateString(undefined, { weekday: "short" }), count: 0 };
    });
    const dayIndex = Object.fromEntries(byDay.map((b, i) => [b.key, i]));

    for (const l of items) {
      if (byStatus[l.status] !== undefined) byStatus[l.status] += 1;
      const src = l.source || "contact";
      bySource[src] = (bySource[src] || 0) + 1;
      const day = (l.created_at || "").slice(0, 10);
      if (day in dayIndex) byDay[dayIndex[day]].count += 1;
    }

    return { byStatus, bySource, byDay };
  }, [items]);

  const total = items.length;
  const maxDay = Math.max(1, ...stats.byDay.map((d) => d.count));
  const sourceEntries = Object.entries(stats.bySource).sort((a, b) => b[1] - a[1]);
  const maxSource = Math.max(1, ...sourceEntries.map(([, v]) => v));
  const statusEntries = STATUSES.map((s) => [s, stats.byStatus[s]]);
  const maxStatus = Math.max(1, ...statusEntries.map(([, v]) => v));

  if (!total) return null;

  return (
    <div
      data-testid="admin-leads-overview"
      className="mb-10 border border-line grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-line"
    >
      {/* KPIs */}
      <div className="p-6 md:col-span-1 flex flex-col justify-between">
        <div>
          <p className="eyebrow text-fog">Total leads</p>
          <p className="font-display text-5xl md:text-6xl tracking-tighter text-bone mt-2">{total}</p>
        </div>
        <div className="mt-6 space-y-1 eyebrow text-xs">
          {statusEntries.map(([s, n]) => (
            <div key={s} data-testid={`admin-leads-kpi-${s}`} className="flex items-center justify-between text-fog">
              <span>{s}</span>
              <span className="text-bone tabular-nums">{n}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Source breakdown */}
      <div className="p-6 md:col-span-1">
        <p className="eyebrow text-fog mb-4">◇ By source</p>
        <div className="space-y-3">
          {sourceEntries.length === 0 && <p className="text-fog text-sm">No data yet.</p>}
          {sourceEntries.map(([src, n]) => (
            <div key={src} data-testid={`admin-leads-source-${src}`}>
              <div className="flex items-center justify-between eyebrow text-xs mb-1">
                <span className="text-bone">{src}</span>
                <span className="text-fog tabular-nums">{n}</span>
              </div>
              <div className="h-1.5 bg-charcoal">
                <div
                  className="h-full bg-relatable"
                  style={{ width: `${(n / maxSource) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Status breakdown */}
      <div className="p-6 md:col-span-1">
        <p className="eyebrow text-fog mb-4">◇ By status</p>
        <div className="space-y-3">
          {statusEntries.map(([s, n]) => (
            <div key={s}>
              <div className="flex items-center justify-between eyebrow text-xs mb-1">
                <span className="text-bone">{s}</span>
                <span className="text-fog tabular-nums">{n}</span>
              </div>
              <div className="h-1.5 bg-charcoal">
                <div
                  className="h-full bg-bone"
                  style={{ width: `${(n / maxStatus) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 7-day trend */}
      <div className="p-6 md:col-span-1">
        <p className="eyebrow text-fog mb-4">◇ Last 7 days</p>
        <div className="flex items-end justify-between gap-2 h-24">
          {stats.byDay.map((d) => (
            <div key={d.key} className="flex-1 flex flex-col items-center gap-2" data-testid={`admin-leads-day-${d.key}`}>
              <div className="w-full flex-1 flex items-end">
                <div
                  className="w-full bg-bone transition-all"
                  style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count > 0 ? "3px" : "0" }}
                />
              </div>
              <span className="eyebrow text-fog text-[10px]">{d.label}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 eyebrow text-xs text-fog">
          {stats.byDay.reduce((a, b) => a + b.count, 0)} new this week
        </p>
      </div>
    </div>
  );
}

function LeadsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const res = await api.get("/admin/leads");
    setItems(res.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (lead, status) => {
    await api.patch(`/admin/leads/${lead.id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };
  const onDelete = async (lead) => {
    if (!confirm(`Delete lead from ${lead.name}?`)) return;
    await api.delete(`/admin/leads/${lead.id}`);
    toast.success("Lead deleted");
    load();
  };

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((l) => l.status === filter)),
    [filter, items]
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl tracking-tight">Leads</h2>
          <p className="eyebrow text-fog mt-2">{items.length} total · {items.filter((l) => l.status === "new").length} new</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-leads-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`eyebrow px-3 py-2 border ${filter === s ? "border-bone bg-bone text-ink" : "border-line text-fog hover:text-bone"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <LeadsOverview items={items} />

      <div className="space-y-3">
        {filtered.map((l) => (
          <div key={l.id} data-testid={ADMIN.leadRow} className="border border-line p-6 grid grid-cols-1 md:grid-cols-12 gap-4 hover:bg-charcoal/40 transition-colors">
            <div className="md:col-span-3">
              <p className="font-display text-xl text-bone">{l.name}</p>
              <p className="text-fog text-sm mt-1">{l.email}</p>
              {l.company && <p className="text-fog text-sm">{l.company}</p>}
            </div>
            <div className="md:col-span-2 eyebrow text-fog">
              <p>{l.project_type || "—"}</p>
              <p className="mt-1">{l.budget || "—"}</p>
              <p className="mt-1 text-xs">{l.source}</p>
            </div>
            <div className="md:col-span-5 text-bone/90 leading-relaxed">{l.message}</div>
            <div className="md:col-span-2 flex flex-col items-start md:items-end gap-2">
              <span className={`eyebrow px-2 py-1 ${statusBadgeClass(l.status)}`}>{l.status}</span>
              <p className="eyebrow text-fog text-xs">{new Date(l.created_at).toLocaleString()}</p>
              <div className="flex gap-1 mt-2">
                {STATUSES.filter((s) => s !== l.status).map((s) => (
                  <button key={s} onClick={() => updateStatus(l, s)} className="eyebrow text-xs px-2 py-1 border border-line text-fog hover:text-bone hover:border-bone">{s.slice(0,4)}</button>
                ))}
                <button onClick={() => onDelete(l)} className="eyebrow text-xs px-2 py-1 border border-line text-fog hover:text-red-400 hover:border-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-fog py-16 text-center eyebrow">No leads in this view.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------- UTM Link Builder ---------------- */
const UTM_PRESETS = [
  { label: "Google Ads", source: "google_ads", medium: "cpc", campaign: "brand-films" },
  { label: "Instagram Reel", source: "instagram", medium: "reel", campaign: "founder-story" },
  { label: "Instagram Story", source: "instagram", medium: "story", campaign: "brand-awareness" },
  { label: "LinkedIn Post", source: "linkedin", medium: "post", campaign: "employer-branding" },
  { label: "LinkedIn DM", source: "linkedin", medium: "dm", campaign: "outbound" },
  { label: "WhatsApp Direct", source: "whatsapp", medium: "direct", campaign: "inbound" },
  { label: "Newsletter", source: "newsletter", medium: "email", campaign: "q1-2026" },
  { label: "Podcast Show Notes", source: "podcast", medium: "show-notes", campaign: "founder-appearance" },
];

const UTM_PATHS = ["/contact", "/", "/work", "/about"];
const DEFAULT_BASE_URL = "https://relatable.studio";

function UtmBuilderTab() {
  const [base, setBase] = useState(DEFAULT_BASE_URL);
  const [path, setPath] = useState("/contact");
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("");
  const [campaign, setCampaign] = useState("");
  const [copied, setCopied] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const url = useMemo(() => {
    const clean = (s) => (s || "").trim();
    const params = new URLSearchParams();
    if (clean(source)) params.set("utm_source", clean(source));
    if (clean(medium)) params.set("utm_medium", clean(medium));
    if (clean(campaign)) params.set("utm_campaign", clean(campaign));
    const qs = params.toString();
    const b = base.replace(/\/$/, "");
    return `${b}${path}${qs ? `?${qs}` : ""}`;
  }, [base, path, source, medium, campaign]);

  const applyPreset = (preset) => {
    setSource(preset.source);
    setMedium(preset.medium);
    setCampaign(preset.campaign);
    setCopied(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select the URL and copy manually.");
    }
  };

  const reset = () => {
    setSource(""); setMedium(""); setCampaign(""); setCopied(false);
  };

  const projectPaths = useMemo(
    () => (projects || []).slice(0, 30).map((p) => ({ label: p.title, path: `/work/${p.slug}` })),
    [projects]
  );

  return (
    <div data-testid="admin-utm-builder">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl tracking-tight">UTM Link Builder</h2>
          <p className="eyebrow text-fog mt-2">
            Generate attributed URLs. Landing hits show up as distinct source bars in the Leads chart.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Form */}
        <div className="lg:col-span-7 space-y-6">
          <div>
            <label className="eyebrow text-fog block mb-3">Base URL</label>
            <div className="flex flex-wrap gap-2">
              {[DEFAULT_BASE_URL, "https://www.relatable.studio", window.location.origin].map((b) => (
                <button
                  key={b}
                  onClick={() => setBase(b)}
                  data-testid={`utm-base-${b.replace(/https?:\/\//, "").replace(/\./g, "-")}`}
                  className={`eyebrow px-3 py-2 border transition-colors ${
                    base === b ? "border-bone bg-bone text-ink" : "border-line text-fog hover:text-bone"
                  }`}
                >
                  {b.replace(/^https?:\/\//, "")}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="eyebrow text-fog block mb-3">Landing page</label>
            <div className="flex flex-wrap gap-2">
              {UTM_PATHS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPath(p)}
                  data-testid={`utm-path-${p.replace(/\//g, "-") || "home"}`}
                  className={`eyebrow px-3 py-2 border transition-colors ${
                    path === p ? "border-bone bg-bone text-ink" : "border-line text-fog hover:text-bone"
                  }`}
                >
                  {p}
                </button>
              ))}
              {projectPaths.length > 0 && (
                <select
                  value={UTM_PATHS.includes(path) ? "" : path}
                  onChange={(e) => e.target.value && setPath(e.target.value)}
                  data-testid="utm-path-project-select"
                  className="eyebrow px-3 py-2 border border-line bg-transparent text-fog hover:text-bone appearance-none"
                  style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%23A1A1A1' fill='none' stroke-width='1.5'/></svg>\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center", paddingRight: "28px" }}
                >
                  <option value="" className="bg-ink">— pick a project page —</option>
                  {projectPaths.map((pp) => (
                    <option key={pp.path} value={pp.path} className="bg-ink">{pp.label}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="utm-source-input" className="eyebrow text-fog block mb-3">utm_source</label>
              <input
                id="utm-source-input"
                data-testid="utm-source-input"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="google_ads"
                maxLength={60}
                className="w-full bg-transparent border-b border-line py-2 focus:border-relatable outline-none text-bone text-lg"
              />
            </div>
            <div>
              <label htmlFor="utm-medium-input" className="eyebrow text-fog block mb-3">utm_medium</label>
              <input
                id="utm-medium-input"
                data-testid="utm-medium-input"
                value={medium}
                onChange={(e) => setMedium(e.target.value)}
                placeholder="cpc / reel / email"
                maxLength={100}
                className="w-full bg-transparent border-b border-line py-2 focus:border-relatable outline-none text-bone text-lg"
              />
            </div>
            <div>
              <label htmlFor="utm-campaign-input" className="eyebrow text-fog block mb-3">utm_campaign</label>
              <input
                id="utm-campaign-input"
                data-testid="utm-campaign-input"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="brand-films-q1"
                maxLength={150}
                className="w-full bg-transparent border-b border-line py-2 focus:border-relatable outline-none text-bone text-lg"
              />
            </div>
          </div>

          <div>
            <p className="eyebrow text-fog mb-3">◇ Presets</p>
            <div className="flex flex-wrap gap-2">
              {UTM_PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p)}
                  data-testid={`utm-preset-${p.source}-${p.medium}`}
                  className="eyebrow px-3 py-2 border border-line text-fog hover:text-bone hover:border-bone transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview + Copy */}
        <div className="lg:col-span-5">
          <div className="border border-line p-6 bg-charcoal sticky top-24">
            <p className="eyebrow text-fog mb-4">◇ Generated URL</p>
            <div
              data-testid="utm-generated-url"
              className="font-mono text-sm text-bone break-all bg-ink border border-line p-4 min-h-[100px]"
            >
              {url}
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={copy}
                disabled={!source && !medium && !campaign}
                data-testid="utm-copy-btn"
                className="flex-1 eyebrow px-5 py-3 bg-bone text-ink hover:bg-relatable hover:text-bone transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {copied ? "✓ Copied!" : "Copy URL"}
              </button>
              <button
                onClick={reset}
                data-testid="utm-reset-btn"
                className="eyebrow px-5 py-3 border border-line text-fog hover:text-bone hover:border-bone transition-colors"
              >
                Reset
              </button>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="utm-open-btn"
              className="mt-3 block text-center eyebrow text-fog hover:text-bone transition-colors"
            >
              Open in new tab ↗
            </a>

            <div className="mt-6 pt-6 border-t border-line">
              <p className="eyebrow text-xs text-fog mb-2">Tips</p>
              <ul className="text-fog text-xs leading-relaxed space-y-1.5">
                <li>· <span className="text-bone">Source</span> = where the click came from (google, instagram)</li>
                <li>· <span className="text-bone">Medium</span> = what type of link (cpc, reel, email)</li>
                <li>· <span className="text-bone">Campaign</span> = why you ran it (brand-launch)</li>
                <li>· Use <span className="text-bone">lowercase &amp; hyphens</span> — <span className="text-bone">instagram</span> and <span className="text-bone">Instagram</span> become separate sources.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

